import { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { http } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { PageHero } from "../components/PageHero";
import { Reveal, SectionHead, Figure } from "../components/Bits";
import { QuoteForm } from "../components/QuoteForm";
import { IMAGES } from "../lib/images";

const WHY = [
  {
    title: "Training reaches the walls",
    copy: "Takedowns, scrambles and footwork drills drift towards the perimeter. Padding acknowledges how the room is actually used.",
  },
  {
    title: "Corners and columns",
    copy: "Hard corners and structural columns inside a training area are the highest-risk points in most facilities.",
  },
  {
    title: "A finished facility",
    copy: "Beyond protection, a padded wall line gives the room a consistent, considered appearance.",
  },
];

const TYPES = [
  {
    title: "Flat wall panels",
    copy: "The standard system for straight runs of wall along the working area.",
    image: IMAGES.wall_pads,
  },
  {
    title: "Corner and column wraps",
    copy: "Dedicated wrapped sections that remove exposed hard edges inside the room.",
    image: IMAGES.corner_column,
  },
  {
    title: "Zoned and branded panels",
    copy: "Colour bands, printed logos and lettering built into the panel layout.",
    image: IMAGES.mma_room,
  },
];

export default function WallPads() {
  const [product, setProduct] = useState(null);

  useSeo({
    title: "Martial Arts Wall Pads | Gym Wall Padding India | MAKO Mats",
    description:
      "Protective wall padding for MMA, wrestling and martial arts facilities in India. Corner and column wraps, custom colours and printed branding.",
  });

  useEffect(() => {
    http.get("/content/products/wall-pads").then((r) => setProduct(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="wall-pads-page">
      <PageHero
        eyebrow="MAKO Wall"
        title="Protect the whole training space"
        copy="Professional wall padding for safer movement, cleaner facilities and complete combat-room installations."
        image={IMAGES.wall_pads}
        ctas={[
          { label: "Request a quote", to: "/contact", testId: "wall-quote-cta" },
          {
            label: "View installations",
            to: "/installations",
            variant: "outline",
            testId: "wall-installations-cta",
          },
        ]}
      />

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Why it matters"
            title="Why wall padding matters"
            copy="Wall protection is part of the surface system, not an accessory. It is easiest to specify at the same time as the flooring."
          />
          <div className="mt-12 grid gap-px border border-[#111111]/10 bg-[#111111]/10 sm:grid-cols-3">
            {WHY.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="h-full bg-white p-8 lg:p-10">
                  <p className="mako-label text-[#0F4C81]">0{i + 1}</p>
                  <p className="mt-6 font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                    {w.title}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-[#3B3B3B]">{w.copy}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-y border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead
            eyebrow="Systems"
            title="Wall-pad types"
            copy="Panel configurations are planned to the wall dimensions and to how close training happens to each surface."
          />
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {TYPES.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.07}>
                <Figure src={t.image} alt={t.title} ratio="aspect-[4/5]" />
                <p className="mt-6 font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                  {t.title}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-[#3B3B3B]">{t.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <div className="lg:col-span-6">
            <SectionHead eyebrow="Applications" title="Suitable applications" />
            <ul className="mt-8 space-y-3">
              {(product?.best_for || []).map((b) => (
                <li key={b} className="flex gap-3 text-sm text-[#3B3B3B]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C81]" />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6">
            <SectionHead eyebrow="Customisation" title="Colour, logo and branding options" />
            <ul className="mt-8 space-y-3">
              {(product?.customisation || []).map((c) => (
                <li key={c} className="border-l-2 border-[#0F4C81] pl-4 text-sm text-[#3B3B3B]">
                  {c}
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <Figure
                src={IMAGES.swatches}
                alt="Colour swatch samples"
                ratio="aspect-[16/9]"
                caption="Colour and finish options"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-[#111111]/10 bg-[#F5F5F2] py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
          <SectionHead eyebrow="Gallery" title="Installation gallery" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[IMAGES.wall_pads, IMAGES.corner_column, IMAGES.branded_wall_pad, IMAGES.multi_zones].map(
              (src, i) => (
                <Reveal key={src} delay={i * 0.06}>
                  <Figure src={src} alt={`Wall padding installation ${i + 1}`} ratio="aspect-[4/5]" />
                </Reveal>
              )
            )}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10">
          <div className="lg:col-span-4">
            <SectionHead
              eyebrow="Request a quote"
              title="Plan your wall protection"
              copy="Send us wall lengths, heights and photographs of the room. We will plan the panel layout with you."
            />
          </div>
          <div className="lg:col-span-8">
            <QuoteForm source="wall-pads" />
          </div>
        </div>
      </section>
    </div>
  );
}
